<template>
  <div>
    <tableComponent
      class="pa-5 pl-0"
      ref="tableComponent"
      :tableSettings="tableSettings"
      :tableData="tableData"
      :headers="headers"
      :enable-row-clickable="true"
      @newDocument="newDocument"
      @import="openImport"
      @clickedRow="openDetails($event)"
      @openMultiUserDialog="openMultiUserDialog"
      @openCreateRole="openCreateRoleDialog"
      @openBulkTagDialog="openBulkTagDialog"
      @openBulkRoleDialog="openBulkRoleDialog"
      @confirmBulkArchive="confirmBulkArchive"
      @confirmBulkDelete="confirmBulkDelete"
    />
    <employeeBulkTagDialog ref="bulkTagDialog" @applied="onBulkActionDone" />
    <employeeBulkRoleDialog ref="bulkRoleDialog" @applied="onBulkActionDone" />
    <v-dialog v-model="bulkConfirm.open" max-width="440">
      <v-card>
        <v-card-title>{{ bulkConfirm.title }}</v-card-title>
        <v-card-text>{{ bulkConfirm.message }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="bulkConfirm.open = false" :disabled="bulkConfirm.loading">
            {{ $t("general.cancel") }}
          </v-btn>
          <v-btn
            :color="bulkConfirm.color"
            :loading="bulkConfirm.loading"
            @click="runBulkConfirm"
          >
            {{ bulkConfirm.confirmText }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <createRoleDialog v-model="createRoleOpen" @created="onRoleCreated" />
    <importDialog @triggerUpload="importData" :importDialog="importDialog" />
    <employeeDetailsDialog
      ref="employeeDetailsDialog"
      v-model="isEmployeeDialogOpen"
    />
    <multiUserDialog
      v-model="multiUserOpen"
      entityType="employee"
      :availableItems="transferItems"
      itemText="name"
      itemValue="_id"
      :clients="transferClients"
      :currentClientId="currentClientId"
      @confirm="executeTransfer"
      entityLabel="multiUser.dialog.employees"
    />
  </div>
</template>
<script>
import { getUniqueId } from "@/components/api/api";
import tableComponent from "@/components/_utils/dataTableHelpers/tableComponent.vue";
import importDialog from "./importDialog.vue";
import employeeDetailsDialog from "../../components/tableSlotComponents/employee/employeeDetailsDialog.vue";
import multiUserDialog from "../../components/_utils/dataTableHelpers/actions/multiUserDialog.vue";
import createRoleDialog from "../../components/_utils/dataTableHelpers/actions/createRoleDialog.vue";
import employeeBulkTagDialog from "../../components/tableSlotComponents/employee/employeeBulkTagDialog.vue";
import employeeBulkRoleDialog from "../../components/tableSlotComponents/employee/employeeBulkRoleDialog.vue";

export default {
  components: {
    tableComponent,
    importDialog,
    employeeDetailsDialog,
    multiUserDialog,
    createRoleDialog,
    employeeBulkTagDialog,
    employeeBulkRoleDialog,
  },
  data: () => ({
    tableSettings: {
      title: "",
      showSelect: true,
      showHeaderToolbar: true,
      headerButtons: [
        {
          title: "multiUser.button",
          color: "primary",
          action: "openMultiUserDialog",
          icon: "mdi-swap-horizontal",
          iconDark: true,
          dark: true,
        },
        {
          title: "employee.createRole",
          color: "secondary",
          action: "openCreateRole",
          icon: "add",
          iconDark: true,
          dark: true,
          outlined: true,
        },
        {
          title: "employee.importEmployee",
          color: "grey",
          action: "import",
          icon: "download",
          iconDark: true,
          dark: true,
          outlined: true,
        },
        {
          title: "employee.addEmployee",
          color: "secondary",
          action: "newDocument",
          icon: "add",
          iconDark: true,
          dark: true,
          outlined: true,
        },
      ],
      multipleSelectButtons: [
        {
          title: "employee.tags.bulkButton",
          color: "primary",
          action: "openBulkTagDialog",
          icon: "mdi-tag-multiple",
          iconDark: true,
          dark: true,
          outlined: true,
        },
        {
          title: "employee.bulk.roleButton",
          color: "primary",
          action: "openBulkRoleDialog",
          icon: "mdi-account-plus-outline",
          iconDark: true,
          dark: true,
          outlined: true,
        },
        {
          title: "employee.bulk.archiveButton",
          color: "grey darken-1",
          action: "confirmBulkArchive",
          icon: "archive",
          iconDark: true,
          dark: true,
          outlined: true,
        },
        {
          title: "employee.bulk.deleteButton",
          color: "error",
          action: "confirmBulkDelete",
          icon: "delete",
          iconDark: true,
          dark: true,
          outlined: true,
        },
      ],
    },
    tableData: {
      tableComponentSlots: [
        {
          id: getUniqueId(),
          slotName: "item.employeeAction",
          componentName: "employeeAction",
        },
        {
          id: getUniqueId(),
          slotName: "item.employeeTags",
          componentName: "employeeTagsColumn",
        },
      ],
      itemKey: "_id",
      uriPartPrepend: "employee",
      uriPartAppend: "",
      populate: ["tags"],
      sort: "",
      editFormPushName: "employeeForm",
    },
    importDialog: {
      open: false,
      title: "employee.importEmployeeTitle",
      description: "",
    },
    isEmployeeDialogOpen: false,
    createRoleOpen: false,
    bulkConfirm: {
      open: false,
      loading: false,
      title: "",
      message: "",
      confirmText: "",
      color: "primary",
      action: null,
      ids: [],
    },
    currentClientId: "",
    isMultiUser: false,
    multiUserOpen: false,
    transferClients: [],
    transferItems: [],
  }),

  async created() {
    const user = await this.$store.dispatch("getUserInfo");
    this.currentClientId = user?.client?._id || "";
    this.isMultiUser = user?.role === "MultiUser";

    this.transferItems = await this.$store.dispatch("getAllEmployees");

    if (!this.isMultiUser) {
      this.tableSettings.headerButtons =
        this.tableSettings.headerButtons.filter(
          (btn) => btn.action !== "openMultiUserDialog",
        );
    }

    this.$store.dispatch("getEmployeeRoleDefinitions");

  },
  computed: {
    headers() {
      return [
        {
          text: this.$t("employee.tableHeader.name"),
          value: "name",
          align: "left",
        },
        { text: this.$t("employee.tableHeader.email"), value: "email" },
        {
          text: this.$t("employee.tableHeader.language"),
          value: "employeeLanguage",
        },
        {
          text: this.$t("employee.tableHeader.tags"),
          value: "employeeTags",
        },
        {
          text: this.$t("employee.tableHeader.organization"),
          value: "employeeAction",
          align: "center",
          sortable: true,
        },
      ];
    },

  },

  methods: {
    // methods
    async openMultiUserDialog() {
      try {
        const res = await this.$store.dispatch("getTransferClients");
        this.transferClients = res.data || [];
      } catch (e) {
        this.showTransferSnackbar(
          this.$t("multiUser.dialog.clientLoadError"),
          "error",
        );
        return;
      }

      this.multiUserOpen = true;
    },
    async executeTransfer(payload) {
      try {
        const result = await this.$store.dispatch("transferEntities", payload);

        // Build result message
        const parts = [];
        if (result.totalSuccess > 0) {
          parts.push(
            `${result.totalSuccess} ${this.$t("multiUser.result.success")}`,
          );
        }
        if (result.totalConflict > 0) {
          parts.push(
            `${result.totalConflict} ${this.$t("multiUser.result.errors")}`,
          );
        }
        if (result.totalError > 0) {
          parts.push(
            `${result.totalError} ${this.$t("multiUser.result.errors")}`,
          );
        }

        this.$store.commit("CREATE_SNACKBAR", parts.join(" · "));

        if (payload.action === "move" && result.totalSuccess > 0) {
          this.reloadDataFunc();
          this.fetchStats();
        }
      } catch (e) {
        this.$store.dispatch("triggerError", e);
      }
    },

    reloadDataFunc() {
      this.$refs.tableComponent.getData();
    },
    openBulkTagDialog(selectedItems) {
      this.$refs.bulkTagDialog.open(selectedItems || []);
    },
    openBulkRoleDialog(selectedItems) {
      this.$refs.bulkRoleDialog.open(selectedItems || []);
    },
    confirmBulkArchive(selectedItems) {
      const ids = (selectedItems || []).map((e) => e._id);
      this.bulkConfirm = {
        open: true,
        loading: false,
        title: this.$t("employee.bulk.archiveTitle"),
        message: this.$t("employee.bulk.archiveConfirm", { count: ids.length }),
        confirmText: this.$t("employee.bulk.archiveButton"),
        color: "primary",
        action: "bulkArchiveEmployees",
        ids,
      };
    },
    confirmBulkDelete(selectedItems) {
      const ids = (selectedItems || []).map((e) => e._id);
      this.bulkConfirm = {
        open: true,
        loading: false,
        title: this.$t("employee.bulk.deleteTitle"),
        message: this.$t("employee.bulk.deleteConfirm", { count: ids.length }),
        confirmText: this.$t("employee.bulk.deleteButton"),
        color: "error",
        action: "bulkDeleteEmployees",
        ids,
      };
    },
    async runBulkConfirm() {
      this.bulkConfirm.loading = true;
      try {
        await this.$store.dispatch(this.bulkConfirm.action, this.bulkConfirm.ids);
        this.bulkConfirm.open = false;
        this.onBulkActionDone();
      } finally {
        this.bulkConfirm.loading = false;
      }
    },
    onBulkActionDone() {
      if (this.$refs.tableComponent) {
        this.$refs.tableComponent.clearSelectItems();
      }
      this.reloadDataFunc();
      this.fetchStats();
      this.$root.$emit("employee-stats-refresh");
    },
    newDocument() {
      this.$router.push({
        name: this.tableData.editFormPushName,
        params: { id: "1", mode: "new", data: 1 },
      });
    },
    deleteItem(item) {
      this.$store.dispatch("deleteMultipleOperatingemployee", item).then(() => {
        this.reloadDataFunc();
        this.multipleDeleteDialog.open = false;
        this.multipleDeleteDialog.items = [];
      });
    },
    openImport() {
      this.importDialog.open = true;
    },
    openCreateRoleDialog() {
      this.createRoleOpen = true;
    },
    onRoleCreated() {
      this.$store.dispatch("getEmployeeRoleDefinitions");
      this.fetchStats();
    },
    importData(item) {
      this.$store
        .dispatch("createEmployeeImport", { file: item.content })
        .then(() => {
          this.reloadDataFunc();
          this.importDialog.open = false;
        });
    },
    openDetails(employee) {
      this.$refs.employeeDetailsDialog.data = employee;
      this.isEmployeeDialogOpen = true;
    },

  },
};
</script>

<style>

</style>
